import React from 'react';

interface PatientAvatarProps {
    name: string;
    mrn?: string;
    size?: 'sm' | 'md' | 'lg';
    className?: string;
}

const getInitials = (name: string) => {
    if (!name) return '??';
    return name
        .split(' ')
        .map(n => n[0])
        .slice(0, 2)
        .join('')
        .toUpperCase();
};

const getColorFromName = (name: string) => {
    // Use inline hex colors instead of Tailwind classes — classes like
    // bg-violet-500 get purged by Tailwind's tree-shaking because they
    // aren't used literally in templates, causing transparent avatars.
    const colors = [
        '#3b82f6', // blue-500
        '#10b981', // emerald-500
        '#8b5cf6', // violet-500
        '#f59e0b', // amber-500
        '#f43f5e', // rose-500
        '#06b6d4', // cyan-500
        '#6366f1', // indigo-500
        '#ec4899', // pink-500
    ];

    let hash = 0;
    for (let i = 0; i < name.length; i++) {
        hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }

    return colors[Math.abs(hash) % colors.length];
};

const PatientAvatar: React.FC<PatientAvatarProps> = ({
    name,
    mrn,
    size = 'md',
    className = ''
}) => {
    const initials = getInitials(name);
    const bgColor = getColorFromName(name || mrn || '');

    const sizeClasses = {
        sm: 'w-8 h-8 text-[14px]',
        md: 'w-10 h-10 text-sm',
        lg: 'w-12 h-12 text-base'
    };

    return (
        <div
            className={`
                flex items-center justify-center
                rounded-full text-white font-bold
                shadow-sm ring-2 ring-white
                ${sizeClasses[size]}
                ${className}
            `}
            style={{ backgroundColor: bgColor }}
        >
            {initials}
        </div>
    );
};

export default PatientAvatar;
